import { IVisibleLogic } from 'config/loan.input.visibility.type'
import { useEffect, useState } from 'react'
import {
  type ReviewFormReport,
  type ReviewFormReporterSection as IReviewFormReporterSection,
  getReviewFormCategories,
  getReviewFormReporterSection,
} from 'services'
import { svgLoading } from 'stories/assets'
import { Button, ButtonGroup } from 'stories/components'

import { AppraisalReviewFieldsConfig } from './AppraisalReviewFieldsConfig'
import { ReviewFormCategories } from './ReviewFormCategories'
import { ReviewFormDetails } from './ReviewFormDetails'
import { ReviewFormReportDetails } from './ReviewFormReportDetails'
import { ReviewFormReporterSection } from './ReviewFormReporterSection'
import { ReviewFormTemplate, ReviewFormTemplates } from './ReviewFormTemplates'

export interface ReviewFormCategory {
  id: string
  value: string | null
  visibleLogic?: IVisibleLogic[][]
  visible?: boolean
}

export const ReviewFormItems = () => {
  const [isLoading, setLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [currentTab, setCurrentTab] = useState('Templates')
  const [categories, setCategories] = useState<ReviewFormCategory[]>([])
  const [values, setValues] = useState<ReviewFormTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<ReviewFormTemplate | null>(null)
  const [reporterData, setReporterData] = useState<IReviewFormReporterSection>({ label1: '', reports: [] })
  const [reporterLoading, setReporterLoading] = useState(false)
  const [isEditingReport, setIsEditingReport] = useState(false)
  const [selectedReport, setSelectedReport] = useState<ReviewFormReport | null>(null)

  useEffect(() => {
    setLoading(true)
    getReviewFormCategories().then((values: ReviewFormCategory[]) => {
      setCategories(values)
      setLoading(false)
    })
    setReporterLoading(true)
    getReviewFormReporterSection()
      .then((v) => setReporterData(v))
      .finally(() => setReporterLoading(false))
  }, [])

  const onAdd = () => {
    setIsEditing(true)
    setSelectedTemplate(null)
  }

  const onEdit = (template: ReviewFormTemplate) => {
    setIsEditing(true)
    setSelectedTemplate(template)
  }

  const onAddReport = () => {
    setSelectedReport(null)
    setIsEditingReport(true)
  }

  const onEditReport = (report: ReviewFormReport) => {
    setSelectedReport(report)
    setIsEditingReport(true)
  }

  if (isEditing)
    return <ReviewFormDetails categories={categories} defaults={selectedTemplate} onBack={() => setIsEditing(false)} />

  if (isEditingReport)
    return (
      <ReviewFormReportDetails
        defaults={selectedReport}
        reporterData={reporterData}
        setReporterData={setReporterData}
        onBack={() => setIsEditingReport(false)}
      />
    )

  return (
    <div>
      <h1 className="text-2xl font-bold flex items-center pb-5">
        Valuation Review Template
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h1>

      <div className="flex justify-between mb-6">
        <ButtonGroup
          title={['Templates', 'Categories', "Reviewer's Report", 'Appraisal Review Fields']}
          value={currentTab}
          onChange={(v) => setCurrentTab(v)}
        />
        <div className="w-32">
          {currentTab === 'Templates' && (
            <Button full onClick={onAdd}>
              Add
            </Button>
          )}
          {currentTab === "Reviewer's Report" && (
            <Button full onClick={onAddReport}>
              Add
            </Button>
          )}
        </div>
      </div>

      {currentTab === 'Categories' && (
        <ReviewFormCategories
          setLoading={setLoading}
          isLoading={isLoading}
          categories={categories}
          setCategories={setCategories}
          values={values}
        />
      )}
      {currentTab === 'Templates' && (
        <ReviewFormTemplates
          setLoading={setLoading}
          isLoading={isLoading}
          categories={categories}
          setCategories={setCategories}
          onEdit={onEdit}
          values={values}
          setValues={setValues}
        />
      )}
      {currentTab === "Reviewer's Report" && (
        <ReviewFormReporterSection
          data={reporterData}
          setData={setReporterData}
          isLoading={reporterLoading}
          setLoading={setReporterLoading}
          onEdit={onEditReport}
        />
      )}
      {currentTab === 'Appraisal Review Fields' && <AppraisalReviewFieldsConfig />}
    </div>
  )
}
